import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { UtilitySharedService } from '../../../../../services/shared/utility-shared.service';
import { PowerTransformerService } from '../../../../../services/power-transformer.service';
import { ToastrService } from 'ngx-toastr';
import {
  UploadFileInfo,
  UploadFileType,
} from '../../../../../models/uploadFileInfo.model';
import { UploadFileInfoService } from '../../../../../services/upload-file-info.service';
import { CimSubstationService } from '../../../../../services/cim-substation.service';
import { AcLineSegmentService } from '../../../../../services/ac-line-segment.service';

@Component({
  selector: 'lib-nec-upload-modal-power-transformer',
  templateUrl: './nec-upload-modal-power-transformer.component.html',

  styleUrls: ['./nec-upload-modal-power-transformer.component.scss'],

  standalone: false,
})
export class NecUploadModalPowerTransformerComponent implements OnInit {
  constructor(
    private powerTransformerService: PowerTransformerService,
    private cimSubstationService: CimSubstationService,
    private acLineSegmentService: AcLineSegmentService,
    private utilitySharedService: UtilitySharedService,
    private toastr: ToastrService,
    private uploadFileService: UploadFileInfoService,
  ) {
    this.utilityId = this.utilitySharedService.getUtilityId() ?? 0;
  }

  @Input() uploadType!: UploadFileType;
  @Input() substationTemplateId: number = 0;
  @Input() module: string = '';

  uploads: UploadFileInfo[] = [];
  count: number = 0;
  utilityId: number;
  uploadFile: File | null = null;
  file: string | ArrayBuffer = '';
  currentPage: number = 1;
  pageSize: number = 7;

  description: string = '';
  serviceUploadOrder: string = '';
  showDescriptionPopup: boolean = false;

  ngOnInit(): void {
    this.reloadTable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['uploadType'] && !changes['uploadType'].firstChange) {
      this.reloadTable();
    }
  }

  onFileSelected(event: any, fileInput: HTMLInputElement) {

    this.uploadFile = event.target.files.item(0);
    const reader = new FileReader();
    this.file = this.uploadFile!.name;
    reader.readAsDataURL(this.uploadFile!);

    fileInput.value = '';

    this.openDescriptionPopup();
  }

  openDescriptionPopup() {
    this.description = '';
    this.serviceUploadOrder = '';
    this.showDescriptionPopup = true;
  }

  cancelUpload() {
    this.showDescriptionPopup = false;
    this.uploadFile = null;
    this.file = '';
    this.description = '';
    this.serviceUploadOrder = '';
  }

  confirmUpload() {
    this.showDescriptionPopup = false;
    this.handleUpload();
  }

  handleUpload() {
    let data = new FormData();
    data.append('file', this.uploadFile!, this.uploadFile?.name);
    data.append('utilityId', this.utilityId.toString());
    data.append('description', this.description);
    data.append('serviceUploadOrder', this.serviceUploadOrder);
    if (this.uploadType === UploadFileType.Substations)
      data.append('substationTemplateId', this.substationTemplateId.toString());
    if (this.uploadType === UploadFileType.ACLine)
      data.append('module', this.module);
    switch (this.uploadType) {
      case UploadFileType.CimPowerTransformer:
        this.uploadTransformers(data);
        break;
      case UploadFileType.Substations:
        this.uploadSubstationFile(data);
        break;
      case UploadFileType.ACLine:
        this.uploadAcLines(data);
        break;
      case UploadFileType.UpdateFeederBands:
        this.updateFeederBands(data);
        break;
      case UploadFileType.BulkFeederReassignment:
        this.bulkFeederReassignment(data);
        break;
      case UploadFileType.BulkSubstationUpdate:
        this.bulkSubstationUpdate(data);
        break;
      case UploadFileType.BulkAcLineUpdate:
        this.bulkAcLineUpdate(data);
        break;
    }
  }

  uploadTransformers(data: FormData) {
    this.powerTransformerService.uploadFile(data).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0)
            window.location.href = response.data;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
    });
  }

  uploadSubstationFile(data: FormData) {
    this.cimSubstationService.uploadFile(data).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0)
            window.location.href = response.data;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      }
    });
  }

  uploadAcLines(data: FormData) {
    this.acLineSegmentService.uploadFile(data).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0)
            window.location.href = response.data;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
    });
  }

  updateFeederBands(data: FormData) {
    this.acLineSegmentService.updateFeederBands(data).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0)
            window.location.href = response.data;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
    });
  }

  bulkFeederReassignment(data: FormData) {
    this.cimSubstationService.bulkFeederReassignment(data).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0)
            window.location.href = response.data;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
    });
  }

  bulkSubstationUpdate(data: FormData) {
    this.cimSubstationService.bulkSubstationUpdate(data).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0)
            window.location.href = response.data;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
    });
  }

  bulkAcLineUpdate(data: FormData) {
    this.acLineSegmentService.bulkAcLineUpdate(data).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0)
            window.location.href = response.data;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
    });
  }

  reloadTable(page: any = null) {
    if (page) this.currentPage = page;

    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },

      UploadFileType: this.uploadType,
      utilityId: this.utilityId,
    };

    this.getByType(obj);
  }

  pageChange(value: any) {
    this.currentPage = value;
    this.reloadTable();
  }

  downloadTemplate() {
    switch (this.uploadType) {
      case UploadFileType.CimPowerTransformer:
        this.getPowerTransformersTemplate();
        break;
      case UploadFileType.Substations:
        this.getCimSubstationsTemplate();
        break;
      case UploadFileType.ACLine:
        this.getAcLinesTemplate();
        break;
      case UploadFileType.UpdateFeederBands:
        this.getFeederBandUpdateTemplate();
        break;
      case UploadFileType.BulkFeederReassignment:
        this.getFeederReassignmentTemplate();
        break;
      case UploadFileType.BulkSubstationUpdate:
        this.getBulkSubstationUpdateTemplate();
        break;
      case UploadFileType.BulkAcLineUpdate:
        this.getBulkAcLineUpdateTemplate();
        break;
    }
  }

  getAcLinesTemplate() {
    this.acLineSegmentService.generateTemplate(this.utilityId).subscribe({
      next: (response: any) => {
        if (response?.body?.status === 'OK' || response?.body?.status === 200) {
          window.location.href = response.body.data;
        }
      },
    });
  }

  getFeederBandUpdateTemplate() {
    this.acLineSegmentService
      .generateFeederBandUpdateTemplate(this.utilityId)
      .subscribe({
        next: (response: any) => {
          if (
            response?.body?.status === 'OK' ||
            response?.body?.status === 200
          ) {
            window.location.href = response.body.data;
          }
        },
      });
  }

  getCimSubstationsTemplate() {
    this.cimSubstationService.generateTemplate(this.utilityId).subscribe({
      next: (response: any) => {
        if (response?.body?.status === 'OK' || response?.body?.status === 200) {
          window.location.href = response.body.data;
        }
      },
    });
  }

  getPowerTransformersTemplate() {
    this.powerTransformerService.generateTemplate(this.utilityId).subscribe({
      next: (response: any) => {
        if (response?.body?.status === 'OK' || response?.body?.status === 200) {
          window.location.href = response.body.data;
        }
      },
    });
  }

  getFeederReassignmentTemplate() {
    this.cimSubstationService.generateFeederReassignTemplate(this.utilityId).subscribe({
      next: (response: any) => {
        if (response?.body?.status === 'OK' || response?.body?.status === 200) {
          window.location.href = response.body.data;
        }
      },
    });
  }

  getBulkSubstationUpdateTemplate() {
    this.cimSubstationService.generateBulkSubstationUpdateTemplate(this.utilityId).subscribe({
      next: (response: any) => {
        if (response?.body?.status === 'OK' || response?.body?.status === 200) {
          window.location.href = response.body.data;
        }
      },
    });
  }

  getBulkAcLineUpdateTemplate() {
    this.acLineSegmentService.generateBulkAcLineUpdateTemplate(this.utilityId).subscribe({
      next: (response: any) => {
        if (response?.body?.status === 'OK' || response?.body?.status === 200) {
          window.location.href = response.body.data;
        }
      },
    });
  }

  getByType(obj: any) {
    this.uploadFileService.getByType(obj).subscribe({
      next: (response) => {
        if (response.status === 'OK' || response.status === '200') {
          this.uploads = response.data.data;
          this.count = response.data.count;
        } else {
          this.toastr.error(response.message);
        }
      },
    });
  }
}
